import { DeleteOutlined, MailOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, Drawer, Form, Input, Modal, notification, Popconfirm, Row, Space, Upload } from 'antd'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { apiDeleteEmailTemplateAttachment, apiGetEmailTemplate, apiRestoreDefaultEmailTemplate, apiSaveEmailTemplate, apiSendEmailTemplate, apiSendTestEmail, apiUploadEmailTemplateAttachment } from '~/api/adminApi'
import BundledEditor from '~/components/BundledEditor'
import { JOIN_TYPE_HANDS_ON, JOIN_TYPE_RECORDING, JOIN_TYPE_VIEWER } from '~/utils/constants'
import { showErrors } from '~/utils/helpers'
import { CourseContext } from '.'
import { EmailTemplatePreview } from './EmailTemplatePreview'

function MessageEditorInput({ value, onChange = (value: any) => { } }: { value?: string, onChange?: (value: string) => void }) {
  return <BundledEditor
    value={value ?? ''}
    onChange={onChange}
  />
}

export const EmailTemplateEditor = ({ isOpen, templateKey }: { isOpen: boolean, templateKey: string }): React.JSX.Element => {
  const { id: courseId } = useParams()
  const showRestoreDefaultButton = courseId != null
  const [form] = Form.useForm<{ title: string, content: string }>()
  const emailTemplateQuery = useQuery({
    enabled: isOpen,
    queryKey: ['email_template', courseId, templateKey],
    queryFn: async () => {
      return await apiGetEmailTemplate(templateKey, courseId)
    },
    refetchOnWindowFocus: false
  })

  const editTemplateMutation = useMutation<unknown, unknown, any, unknown>({
    mutationFn: async (values) => {
      return await apiSaveEmailTemplate(values.id, values)
    },
    onError: showErrors,
    onSuccess: () => {
      notification.success({ title: 'Email template is saved' })
    }
  })

  const previewRef = useRef<HTMLIFrameElement>(null)
  const emailTemplate = emailTemplateQuery.data
  const attachments = emailTemplate?.media ?? []

  const refreshPreview = useCallback(() => previewRef.current?.contentWindow?.location.reload(), [])

  const queryClient = useQueryClient()
  const editTemplate = useCallback(
    (values) => {
      editTemplateMutation.mutate(
        { id: emailTemplate?.id, ...values },
        {
          onSuccess: (data) => {
            refreshPreview()
            queryClient.setQueryData(['email_template', templateKey], data)
          }
        }
      )
    },
    [editTemplateMutation, emailTemplate?.id, queryClient, refreshPreview, templateKey]
  )

  const uploadAttachmentMutatation = useMutation<unknown, unknown, any, unknown>({
    mutationFn: async (file) => {
      await apiUploadEmailTemplateAttachment(emailTemplate?.id, file)
    },
    onSuccess: () => {
      emailTemplateQuery.refetch()
      notification.success({ title: 'Upload file successfully' })
    }
  })

  const deleteAttachmentMutation = useMutation<unknown, unknown, number, unknown>({
    mutationFn: async (fileId) => {
      await apiDeleteEmailTemplateAttachment(emailTemplate?.id, fileId)
    },
    onSuccess: () => {
      emailTemplateQuery.refetch()
    }
  })

  const restoreDefaultTemplateMutation = useMutation<unknown, unknown, any, unknown>({
    mutationFn: async ({ id }) => {
      return await apiRestoreDefaultEmailTemplate(id)
    },
    onSuccess: (data: any) => {
      queryClient.setQueryData(['email_template', templateKey], data)
      form.setFieldsValue({ content: data?.content })
      refreshPreview()
      notification.success({ title: 'Restore default template successfully' })
    }
  })

  const restoreDefaultTemplate = useCallback(() => {
    Modal.confirm({
      content: 'The template will be restored to default content immediately, please confirm',
      onOk: () => {
        restoreDefaultTemplateMutation.mutate({ id: emailTemplate?.id })
      }
    })
  }, [emailTemplate?.id, restoreDefaultTemplateMutation])

  const uploadAttachment = useCallback(({ file }) => {
    uploadAttachmentMutatation.mutate(file)
  }, [uploadAttachmentMutatation])

  const [openTestEmailModal, setOpenTestEmailModal] = useState(false)
  const sendTestEmailMutation = useMutation<unknown, unknown, string, unknown>({
    mutationFn: async (email) => {
      return await apiSendTestEmail(emailTemplate?.id, email)
    },
    onError: showErrors,
    onSuccess: () => {
      notification.success({ title: 'The email has been sent to the provided email address, please check.' })
      setOpenTestEmailModal(false)
    }
  })
  const sendTestEmail = (email) => { sendTestEmailMutation.mutate(email) }

  if (emailTemplate == null || emailTemplateQuery.isFetching) {
    return <div>Loading...</div>
  }

  return (<>
    <Modal title="Send test email"
      open={openTestEmailModal}
      onCancel={() => { setOpenTestEmailModal(false) }}
      okText='Send'
      destroyOnHidden
      okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
      confirmLoading={sendTestEmailMutation.isPending}
      modalRender={(dom) => (
        <Form
          layout="vertical"
          name="form_in_modal"
          onFinish={(values) => {
            sendTestEmail(values.email)
          }}
        >
          {dom}
        </Form>
      )}
    >
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, message: 'Please enter the email!' }]}
      >
        <Input />
      </Form.Item>
    </Modal>
    <Form form={form} onFinish={editTemplate}
      initialValues={{
        title: emailTemplate?.title ?? '',
        content: emailTemplate?.content ?? '',
        cc_addresses: emailTemplate?.cc_addresses ?? '',
        target_user_type: emailTemplate?.target_user_type ?? ''
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Row>
            <Col span={24}>
              <Form.Item name="target_user_type" label="Send to:">
                <Checkbox.Group>
                  <Checkbox value={JOIN_TYPE_HANDS_ON}>Hands-on</Checkbox>
                  <Checkbox value={JOIN_TYPE_VIEWER}>Viewer</Checkbox>
                  <Checkbox value={JOIN_TYPE_RECORDING}>Recording</Checkbox>
                </Checkbox.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name='title' label='Email title:'>
                <Input placeholder='Email title'></Input>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name='cc_addresses' label='Cc:' help="Comma separated">
                <Input placeholder='CC Addresses'></Input>
              </Form.Item>
            </Col>
            <Col span={24} >
              <p>Email content</p>
              <Form.Item name='content'>
                <MessageEditorInput />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={editTemplateMutation.isPending}
                  disabled={editTemplateMutation.isPending}
                >
                  Save
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  onClick={() => { setOpenTestEmailModal(true) }}
                  loading={sendTestEmailMutation.isPending}
                >
                  Send test email
                </Button>

                {showRestoreDefaultButton && <Button
                  type="primary"
                  loading={restoreDefaultTemplateMutation.isPending}
                  disabled={restoreDefaultTemplateMutation.isPending}
                  onClick={() => { restoreDefaultTemplate() }}
                >
                  Restore Default
                </Button>}

                <Upload customRequest={uploadAttachment} showUploadList={false}>
                  <Button type="default" icon={<UploadOutlined />} loading={uploadAttachmentMutatation.isPending}>
                    Upload Attachments
                  </Button>
                </Upload>
              </Space>
            </Col>
          </Row>
          <Row>
            <Col>
              <div style={{ marginTop: 10 }}>Attachments:</div>
              <ul>
                {attachments?.map((item: { name: string, original_url: string, id: any }) => (
                  <li key={item.id}>
                    <a href={item.original_url} target='_blank' rel="noreferrer">
                      {item.name}
                      <Popconfirm
                        title={`Do you want to delete file ${item.name}?`}
                        onConfirm={() => { deleteAttachmentMutation.mutate(item.id) }}
                        okText="YES"
                      >
                        <Button
                          type="text"
                          size='small'
                          icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </a>
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <EmailTemplatePreview ref={previewRef} id={emailTemplate.id} />
        </Col>
      </Row >
    </Form >
  </>
  )
}

export const EmailTemplateButton = ({ title, settingKey, type, showSendButton }: { title: string, settingKey: string, type: string, showSendButton?: boolean }) => {
  const [open, setOpen] = useState(false)
  const course = useContext(CourseContext)

  const drawerTitle = useMemo(() => {
    if (course == null) {
      return <div>{type} - {title}</div>
    }

    return <div>{course?.name} <br /> {type} - {title}</div>
  }, [course, title, type])

  const sendEmailMutation = useMutation<unknown, unknown, { courseId: number, templateType: string }>({
    mutationFn: async (variables) => {
      await apiSendEmailTemplate(variables.courseId, variables.templateType)
    },
    onError: showErrors
  })

  if (course == null) {
    return null
  }

  return (<>
    <Space>
      <Button icon={<MailOutlined />} type='default' onClick={() => { setOpen(true) }}>{title}</Button>
      <Popconfirm
        title={`Do you want to send a ${title} email to users?`}
        onConfirm={() => {
          sendEmailMutation.mutate({ courseId: course?.id, templateType: settingKey })
        }}
        okText="YES"
      >
        {showSendButton && <Button icon={<MailOutlined />} type='primary'>Send</Button>}
      </Popconfirm>
    </Space>

    <Drawer
      title={drawerTitle}
      open={open}
      size={window.innerWidth}
      onClose={() => { setOpen(false) }}
      destroyOnHidden
    >
      <EmailTemplateEditor isOpen={open} templateKey={settingKey} />
    </Drawer>
  </>
  )
}
